
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { Card } from "@/components/ui/card";
import { ConfirmDeleteDialog } from "@/components/tables/ConfirmDeleteDialog";
import { useInvitationActions } from "./invitation/useInvitationActions";
import { InvitationsTable } from "./invitation/InvitationsTable";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoIcon } from "lucide-react";
import { Invitation } from "@/services/employees/invitation/fetchInvitations";

interface InvitationsListProps {
  invitations: Invitation[];
  loading: boolean;
}

export function InvitationsList({ invitations, loading }: InvitationsListProps) {
  const { stores } = useStoreManagementStore();
  const {
    resendingIds,
    deletingIds,
    invitationToDelete,
    handleResendInvitation,
    prepareDeleteInvitation,
    handleDeleteInvitation,
    cancelDeleteInvitation
  } = useInvitationActions();

  // Get store name for display
  const getStoreName = (storeId: string) => {
    return stores.find(store => store.id === storeId)?.name || 'Unknown Store';
  };

  if (loading) {
    return (
      <Card className="mt-6">
        <div className="p-4 border-b">
          <h3 className="text-lg font-medium">Pending Invitations</h3>
        </div>
        <div className="p-4">
          <p>Loading invitations...</p>
        </div>
      </Card>
    );
  }

  console.log("InvitationsList rendering with invitations:", invitations);

  if (!invitations || invitations.length === 0) {
    return (
      <Card className="mt-6">
        <div className="p-4 border-b">
          <h3 className="text-lg font-medium">Pending Invitations</h3>
        </div>
        <div className="p-4">
          <Alert>
            <InfoIcon className="h-4 w-4" />
            <AlertDescription>No pending invitations found</AlertDescription>
          </Alert>
        </div>
      </Card>
    );
  }

  return (
    <Card className="mt-6">
      <div className="p-4 border-b">
        <h3 className="text-lg font-medium">Pending Invitations</h3>
        <p className="text-sm text-muted-foreground mt-1">
          {invitations.length} {invitations.length === 1 ? 'invitation' : 'invitations'} pending
        </p>
      </div>
      
      <InvitationsTable
        invitations={invitations}
        storeNameMap={getStoreName}
        onResendInvitation={handleResendInvitation}
        onDeleteInvitation={prepareDeleteInvitation}
        resendingIds={resendingIds}
        deletingIds={deletingIds}
      />

      {invitationToDelete && (
        <ConfirmDeleteDialog
          open={!!invitationToDelete}
          onOpenChange={(isOpen) => !isOpen && cancelDeleteInvitation()}
          onConfirm={handleDeleteInvitation}
          title="Delete Invitation"
          description={`Are you sure you want to delete the invitation for ${invitationToDelete.first_name} ${invitationToDelete.last_name} (${invitationToDelete.email})? This action cannot be undone.`}
        />
      )}
    </Card>
  );
}
